import {
  getTeamsSearchOptions,
  postRostersByRosterIdPlayersMutation,
  SideType,
} from "@aura-frontend/omnibus-az-client";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  cn,
  Icons,
  isPopulatedArray,
  Skeleton,
  toast,
} from "@win-reality/react-ui";
import { useEffect, useMemo, useRef, useState } from "react";

import { CreatePlayersModal } from "@/components/CreatePlayersModal";
import { useAppContext } from "@/hooks/use-app-context";
import { TEAM_JERSEY_NUMBERS_QUERY_KEY } from "@/hooks/useTeamJerseyNumbers";
import { addOrgPrefix } from "@/lib/utils";
import { GamePlayer, GameTeam } from "@/types/game";
import { splitName } from "@/utils/splitName";

import { TeamRosterPicker } from "../Overview/TeamRosterPicker";
import { BenchPlayerRow, LineupList } from "./LineupList";
import { type SelectTeamSelection } from "./TeamPanel";

type TeamLineupPanelProps = {
  side: TTeamSides;
  team: GameTeam;
  visibilityGroup: string;
  onSelectTeam: (selection: SelectTeamSelection) => void;
  onClearTeam: () => void;
  onSelectPlayer: (type: "pitcher" | "catcher") => void;
  onClearPitcher: () => void;
  onClearCatcher: () => void;
  onAddToLineup: (player: GamePlayer) => void;
  onRemoveFromLineup: (player: GamePlayer) => void;
  onReorderLineup: (newLineup: GamePlayer[]) => void;
  hasMorePlayers?: boolean;
  isLoadingMorePlayers?: boolean;
  isLoadingPlayers?: boolean;
  onLoadMorePlayers?: () => void;
};

export const TeamLineupPanel = ({
  side,
  team,
  visibilityGroup,
  onSelectTeam,
  onClearTeam,
  onSelectPlayer,
  onClearPitcher,
  onClearCatcher,
  onAddToLineup,
  onRemoveFromLineup,
  onReorderLineup,
  hasMorePlayers,
  isLoadingMorePlayers,
  isLoadingPlayers,
  onLoadMorePlayers,
}: TeamLineupPanelProps) => {
  const [isCreatePlayersModalOpen, setIsCreatePlayersModalOpen] =
    useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const { orgId: rawOrgId } = useAppContext();
  const organizationId = rawOrgId ? addOrgPrefix(rawOrgId) : "";
  const queryClient = useQueryClient();
  const addPlayers = useMutation(postRostersByRosterIdPlayersMutation());

  const handlePlayersCreated = async (
    _externalIds: string[],
    createdPlayers: Array<Record<string, unknown>>,
  ) => {
    if (!team.uuid || createdPlayers.length === 0) return;

    const playerInfo = createdPlayers.map((p) => {
      const playerName =
        (p.player_name as string) || (p.playerName as string) || "";
      const { firstName, lastName } = splitName(playerName);
      const hitSide = (
        ((p.hit_side as string) || (p.hitSide as string) || "right") as string
      ).toLowerCase();
      const pitchSide = (
        ((p.pitch_side as string) ||
          (p.pitchSide as string) ||
          "right") as string
      ).toLowerCase();
      const jerseyNumber =
        (p.jerseyNumber as number | string | undefined) ?? undefined;
      return {
        first_name: firstName,
        last_name: lastName,
        bats: hitSide as SideType,
        throws: pitchSide as SideType,
        ...(jerseyNumber !== undefined && {
          jersey_number: parseInt(String(jerseyNumber), 10),
        }),
      };
    });

    // The roster endpoint wants the internal UUID, but team.uuid here is the
    // external ID — look it up via getTeamsSearch first.
    let rosterInternalId: string;
    try {
      const teamSearch = await queryClient.fetchQuery(
        getTeamsSearchOptions({
          query: { externalId: team.uuid, visibilityGroup },
        }),
      );
      rosterInternalId = teamSearch?.teams?.[0]?.id ?? "";
    } catch {
      rosterInternalId = "";
    }
    if (!rosterInternalId) {
      toast("Could not find roster to add players to", { variant: "error" });
      return;
    }

    try {
      await addPlayers.mutateAsync({
        path: { roster_id: rosterInternalId },
        body: { player_info: playerInfo },
      });
      // Jersey numbers are on the roster's members, so they reload too
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ["getPlayersSearch"],
          exact: false,
        }),
        queryClient.refetchQueries({
          queryKey: [TEAM_JERSEY_NUMBERS_QUERY_KEY],
        }),
      ]);
      const word = playerInfo.length === 1 ? "Player" : "Players";
      toast(`${word} added to roster.`, { variant: "success" });
    } catch (error) {
      let message: string | null = null;
      try {
        message = JSON.parse((error as Error).message)?.detail ?? null;
      } catch {
        message = null;
      }
      toast(message ?? "Failed to add players to roster", { variant: "error" });
    }
  };

  const pickerLabel = side === "away" ? "Away Team" : "Home Team";

  const {
    name: teamName,
    players,
    parentName,
    currentLineup,
    currentPitcher,
    currentCatcher,
  } = team;

  const lineupPlayerUuids = useMemo(
    () => new Set(currentLineup.map((p) => p.uuid)),
    [currentLineup],
  );

  const availablePlayers = useMemo(
    () => players.filter((p) => !lineupPlayerUuids.has(p.uuid)),
    [players, lineupPlayerUuids],
  );

  const label = side === "away" ? "Away" : "Home";

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
  const triggerIndex = Math.max(0, availablePlayers.length - 5);

  useEffect(() => {
    if (!hasMorePlayers || !onLoadMorePlayers) return;
    const triggerEl = loadMoreTriggerRef.current;
    if (!triggerEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMorePlayers) {
          onLoadMorePlayers();
        }
      },
      { root: scrollContainerRef.current, threshold: 0 },
    );
    observer.observe(triggerEl);
    return () => observer.disconnect();
  }, [
    hasMorePlayers,
    isLoadingMorePlayers,
    onLoadMorePlayers,
    availablePlayers.length,
  ]);

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="small-scrollbar flex-1 overflow-y-auto px-4 pb-4"
      >
        <div className="border-b border-gray-200 py-4">
          <div className="mt-1 flex items-end justify-between">
            <div>
              <span className="text-sm font-bold text-text-light">{label}</span>
              <div className="text-md text-text-light">{teamName}</div>
              {parentName && <div className="text-sm">{parentName}</div>}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-blue"
                onClick={() => setIsPickerOpen(true)}
              >
                <Icons.PencilOutline />
              </Button>

              <Button
                variant="ghost"
                size="icon-sm"
                className="text-blue"
                onClick={onClearTeam}
              >
                <Icons.Trash />
              </Button>
            </div>
          </div>

          <TeamRosterPicker
            label={pickerLabel}
            value={team.uuid || null}
            initialLabel={team.name}
            initialParentName={team.parentName}
            isOpen={isPickerOpen}
            onOpenChange={setIsPickerOpen}
            onConfirm={(option, visibilityGroup) => {
              if (!option) return;
              onSelectTeam({
                teamUuid: option.value,
                teamName: option.label,
                visibilityGroup,
                parentName: option.meta?.parentName ?? null,
              });
            }}
          />
        </div>

        <div className="border-b border-gray-200 py-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-text-light">
              Starting Pitcher
            </span>
            {!currentPitcher?.uuid && (
              <Button
                variant="secondary-flat-dark-outline"
                size="sm"
                onClick={() => onSelectPlayer("pitcher")}
              >
                Select Pitcher
              </Button>
            )}
          </div>
          {currentPitcher?.uuid && (
            <div className="pt-2">
              <PlayerCard
                player={currentPitcher}
                badgeContent="P"
                badgeType="pitcher"
                onRemove={onClearPitcher}
              />
            </div>
          )}
        </div>

        <div className="border-b border-gray-200 py-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-text-light">
              Starting Catcher
            </span>
            {!currentCatcher?.uuid && (
              <Button
                variant="secondary-flat-dark-outline"
                size="sm"
                onClick={() => onSelectPlayer("catcher")}
              >
                Select Catcher
              </Button>
            )}
          </div>
          {currentCatcher?.uuid && (
            <div className="pt-2">
              <PlayerCard
                player={currentCatcher}
                badgeContent="C"
                badgeType="catcher"
                onRemove={onClearCatcher}
              />
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="mb-2">
            <span className="text-sm font-bold text-text-light">
              Batting Order
            </span>
          </div>

          {!isPopulatedArray(currentLineup) && (
            <div className="text-sm">
              Add players to your batting lineup below.
            </div>
          )}

          <LineupList
            lineupPlayers={currentLineup}
            onRemoveFromLineup={onRemoveFromLineup}
            onReorder={onReorderLineup}
          />
        </div>

        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-bold text-text-light">Bench</span>

            <Button
              variant="secondary-flat-dark-outline"
              size="sm"
              isDisabled={!team.uuid}
              onClick={() => setIsCreatePlayersModalOpen(true)}
            >
              Add Player
            </Button>
          </div>

          <div className="space-y-2">
            {isLoadingPlayers ? (
              <Skeleton className="h-12 rounded" />
            ) : availablePlayers.length === 0 ? (
              <p className="text-sm text-gray-400">No bench players</p>
            ) : (
              <>
                {availablePlayers.map((player, index) => (
                  <div
                    key={player.uuid}
                    ref={
                      index === triggerIndex ? loadMoreTriggerRef : undefined
                    }
                  >
                    <BenchPlayerRow
                      player={player}
                      onAdd={() => onAddToLineup(player)}
                    />
                  </div>
                ))}
                {isLoadingMorePlayers && <Skeleton className="h-12 rounded" />}
              </>
            )}
          </div>
        </div>
      </div>

      <CreatePlayersModal
        open={isCreatePlayersModalOpen}
        onOpenChange={setIsCreatePlayersModalOpen}
        orgId={organizationId}
        onPlayersCreated={handlePlayersCreated}
        successToastMessage={null}
      />
    </div>
  );
};

type PlayerCardProps = {
  player: GamePlayer;
  badgeContent: string;
  badgeType: "pitcher" | "catcher";
  onRemove: () => void;
};

const PlayerCard = ({
  player,
  badgeContent,
  badgeType,
  onRemove,
}: PlayerCardProps) => {
  const displayName = `${player.firstName} ${player.lastName}`;

  return (
    <div className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-2">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded text-sm font-semibold text-white",
            badgeType === "pitcher" && "bg-badge-pitcher",
            badgeType === "catcher" && "bg-badge-catcher",
          )}
        >
          {badgeContent}
        </div>
        <span className="text-sm font-medium text-text-light">
          {displayName}
        </span>
      </div>
      <button
        onClick={onRemove}
        className="rounded p-1 text-accent-dark-3 transition-colors hover:bg-gray-100 hover:text-text-light"
        aria-label={`Remove ${displayName}`}
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};
